import { FIELD_LABELS } from './i18n/deviceFields';

/**
 * One self-contained device-fingerprint snapshot. The shape is intentionally
 * loose so we can serialize whatever the device-overview page has cached at
 * the moment the user clicks 导出 — different ROMs report different fields
 * and we don't want the JSON to silently drop "useful but rare" entries.
 *
 * `groups` is keyed by the dashboard group id (`hardware`, `system`, ...);
 * each group is keyed by `cardId`. Card data is the raw `result` object
 * that the adb-toolbox command returned — i.e. the same shape the dashboard
 * `KvCard` renders.
 */
export interface FingerprintSnapshot {
  generatedAt: string;
  serial: string;
  /** Username that triggered the export (best-effort). */
  user?: string;
  /** Mirrors `device.info` for quick top-of-file context. */
  summary?: {
    model?: string;
    manufacturer?: string;
    release?: string;
    sdk?: number;
    oemSkin?: string;
    oemSkinVersion?: string;
    securityPatch?: string;
  };
  groups: Record<string, Record<string, Record<string, unknown> | undefined>>;
}

/* -------------------------------------------------------------------- */
/* JSON serializer                                                      */
/* -------------------------------------------------------------------- */

/**
 * Produce a stable, pretty-printed JSON string. Empty / undefined card
 * results are dropped so consumers see only fields that the device
 * actually reported. Keys are sorted alphabetically inside each card to
 * keep diffs across snapshots readable.
 */
export function fingerprintToJson(snap: FingerprintSnapshot): string {
  const cleanGroups: Record<string, Record<string, Record<string, unknown>>> = {};
  for (const [gid, cards] of Object.entries(snap.groups)) {
    const cleanCards: Record<string, Record<string, unknown>> = {};
    for (const [cid, card] of Object.entries(cards)) {
      if (!card) continue;
      const sorted = sortObject(card);
      if (Object.keys(sorted).length === 0) continue;
      cleanCards[cid] = sorted;
    }
    if (Object.keys(cleanCards).length > 0) cleanGroups[gid] = cleanCards;
  }
  // Drop empty summary fields so e.g. an offline snapshot doesn't carry
  // `model: undefined` lines.
  const summary = snap.summary
    ? Object.fromEntries(
        Object.entries(snap.summary).filter(([, v]) => v !== undefined && v !== '')
      )
    : undefined;

  const payload: Record<string, unknown> = {
    generatedAt: snap.generatedAt,
    serial: snap.serial,
  };
  if (snap.user) payload.user = snap.user;
  if (summary && Object.keys(summary).length > 0) payload.summary = summary;
  payload.groups = cleanGroups;
  return JSON.stringify(payload, null, 2);
}

function sortObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(obj).sort()) {
    const v = obj[k];
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      out[k] = v;
    } else if (typeof v === 'object') {
      const inner = sortObject(v as Record<string, unknown>);
      if (Object.keys(inner).length > 0) out[k] = inner;
    } else {
      out[k] = v;
    }
  }
  return out;
}

/* -------------------------------------------------------------------- */
/* Markdown serializer                                                  */
/* -------------------------------------------------------------------- */

const GROUP_TITLES: Record<string, string> = {
  hardware: '硬件 · Hardware',
  system: '系统 · System',
  network: '网络 · Network',
  battery: '电池与温度 · Battery & Thermal',
  storage: '存储 · Storage',
  runtime: '运行时 · Runtime',
  security: '安全 & 生态 · Security & Ecosystem',
};

/**
 * Render the snapshot as a Markdown document suitable for pasting into
 * a Notion / Lark / GitHub issue. Top section is a YAML-ish summary
 * (model + Android + skin + security patch); below that we emit one `##`
 * heading per group, with each card as a `###` block of bullet lines.
 *
 * Key design choices:
 *   - Field names use the Chinese label from `deviceFields.ts` when
 *     available, else fall back to the raw key.
 *   - Backticks / pipes / less-thans inside values are escaped — model
 *     strings legitimately contain `>`, ROMs can carry `|` delimiters in
 *     user-agent fingerprints, and code fences are not safe to splice.
 *   - Empty cards are omitted entirely (no "暂无数据" filler).
 */
export function fingerprintToMarkdown(snap: FingerprintSnapshot): string {
  const lines: string[] = [];
  const summary = snap.summary ?? {};
  const title = summary.model || snap.serial;
  lines.push(`# 设备指纹 · ${escapeMd(title)}`);
  lines.push('');

  const summaryBits: string[] = [];
  if (snap.serial) summaryBits.push(`**序列号**: \`${escapeMd(snap.serial)}\``);
  if (summary.manufacturer || summary.model) {
    summaryBits.push(`**机型**: ${escapeMd(`${summary.manufacturer ?? ''} ${summary.model ?? ''}`.trim())}`);
  }
  if (summary.release || summary.sdk !== undefined) {
    summaryBits.push(
      `**系统**: Android ${escapeMd(summary.release ?? '?')} (API ${summary.sdk ?? '?'})`
    );
  }
  if (summary.oemSkin) {
    summaryBits.push(
      `**ROM**: ${escapeMd(summary.oemSkin)}${summary.oemSkinVersion ? ` ${escapeMd(summary.oemSkinVersion)}` : ''}`
    );
  }
  if (summary.securityPatch) {
    summaryBits.push(`**安全补丁**: ${escapeMd(summary.securityPatch)}`);
  }
  summaryBits.push(`**采集时间**: ${escapeMd(snap.generatedAt)}`);
  if (snap.user) summaryBits.push(`**采集人**: ${escapeMd(snap.user)}`);
  for (const b of summaryBits) lines.push(`- ${b}`);
  lines.push('');

  const groupOrder = ['hardware', 'system', 'network', 'battery', 'storage', 'runtime', 'security'];
  const seenGroups = new Set<string>();
  const gids = [...groupOrder.filter((g) => g in snap.groups), ...Object.keys(snap.groups).filter((g) => !groupOrder.includes(g))];
  for (const gid of gids) {
    if (seenGroups.has(gid)) continue;
    seenGroups.add(gid);
    const cards = snap.groups[gid];
    if (!cards) continue;
    const cardEntries = Object.entries(cards).filter(
      ([, v]) => v && Object.keys(v).length > 0
    );
    if (cardEntries.length === 0) continue;
    lines.push(`## ${GROUP_TITLES[gid] ?? gid}`);
    lines.push('');
    for (const [cid, card] of cardEntries) {
      lines.push(`### ${escapeMd(cid)}`);
      for (const [k, v] of Object.entries(card!)) {
        if (v === undefined || v === null || v === '') continue;
        const label = FIELD_LABELS[k];
        const display = label ? `${label} · ${k}` : k;
        lines.push(`- **${escapeMd(display)}**: ${formatMdValue(v)}`);
      }
      lines.push('');
    }
  }
  return lines.join('\n');
}

function escapeMd(s: string): string {
  if (typeof s !== 'string') s = String(s);
  // Escape characters that change Markdown structure when they appear
  // inline. Leave whitespace alone so multi-line strings still flow.
  return s
    .replace(/\\/g, '\\\\')
    .replace(/`/g, '\\`')
    .replace(/\|/g, '\\|')
    .replace(/</g, '\\<')
    .replace(/>/g, '\\>');
}

function formatMdValue(v: unknown): string {
  if (typeof v === 'boolean') return v ? '✓' : '✗';
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return '`[]`';
    if (v.length <= 6 && v.every((x) => typeof x !== 'object')) {
      return v.map((x) => `\`${escapeMd(String(x))}\``).join(', ');
    }
    return `\`[${v.length}] ${v.slice(0, 4).map((x) => (typeof x === 'object' ? '{…}' : escapeMd(String(x)))).join(', ')}…\``;
  }
  if (typeof v === 'object' && v !== null) {
    const keys = Object.keys(v as Record<string, unknown>);
    if (keys.length === 0) return '`{}`';
    if (keys.length <= 4) {
      const inner = keys
        .map((k) => `${escapeMd(k)}=${formatMdValueScalar((v as Record<string, unknown>)[k])}`)
        .join(', ');
      return `\`{ ${inner} }\``;
    }
    return `\`{${keys.length} keys}\``;
  }
  return escapeMd(String(v));
}

function formatMdValueScalar(v: unknown): string {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return Array.isArray(v) ? `[${(v as unknown[]).length}]` : '{…}';
  return String(v);
}

/* -------------------------------------------------------------------- */
/* Browser download helper                                              */
/* -------------------------------------------------------------------- */

export function downloadBlob(name: string, mime: string, body: string): void {
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 200);
}

/** Build a filesystem-safe file name like `adh-fingerprint-<serial>-20260526-123456.json`. */
export function fingerprintFileName(serial: string, ext: 'json' | 'md'): string {
  const safe = serial.replace(/[^A-Za-z0-9_.-]/g, '_').slice(0, 32) || 'unknown';
  // Use compact YYYYMMDD-HHMMSS so the timestamp survives a regex check.
  // toISOString() shape: 2026-05-26T12:34:56.000Z
  const iso = new Date().toISOString();
  const ts = `${iso.slice(0, 10).replace(/-/g, '')}-${iso.slice(11, 19).replace(/:/g, '')}`;
  return `adh-fingerprint-${safe}-${ts}.${ext}`;
}
